/**
 * personal/shared-types.ts — THE JSON API contract of the personal system ("הבית שלנו").
 *
 * Imported by the server (`personal/server/**`) and by the web (`personal/web/src/api.ts`,
 * `api.mock.ts`) via relative path. `docs/api-personal.md` is the prose rendering of this file;
 * when they disagree, this file wins.
 *
 * Conventions (ARCHITECTURE.md §9):
 *  - Money over the wire is INTEGER agorot under keys ending `Agorot` (foreign minor units end
 *    `Minor`). `web/src/format.ts` is the only place that divides by 100.
 *  - Row amounts are SIGNED exactly as Financy delivers them (negative = debit).
 *    Aggregates named `out*`, `spend*`, `charges*`, `fees*`, `used*`, `liabilities*` are
 *    non-negative magnitudes; `net*`, `change*`, `coverage*`, `delta*` are signed.
 *  - Days are `YYYY-MM-DD` strings (Asia/Jerusalem); timestamps are ISO-8601 UTC strings.
 *  - Transactions are addressed by Financy `id` (26 chars), never by `sk`.
 *  - Fields tagged `@computed` are derived by the metrics modules (ARCHITECTURE §8); fields
 *    tagged `@local` are user data stored only in our SQLite; everything else is normalized
 *    Financy data (§4).
 *  - Erasable TypeScript only (types, interfaces, `as const` arrays): the server runs this file
 *    through Node's type stripping, so there are no enums, namespaces or decorators.
 */

// ───────────────────── shared insight (REDESIGN §2/§3) ─────────────────────

/**
 * `summary.narrative` and `summary.urgency` are produced by `shared/insight`, the single
 * implementation of REDESIGN §2 (urgency buckets) and §3 (the written summary) that both systems
 * import (docs/insight-contract.md). Neither server re-implements bucketing, sorting or sentence
 * text; the shapes are re-exported here so the web has one import for the whole contract.
 */
export type {
  Narrative,
  NarrativeKey,
  NarrativePart,
  NarrativeSentence,
  UrgencyBucket,
  UrgencyGroup,
  UrgencyIcon,
  UrgencyItem,
  UrgencyKind,
} from '../shared/insight/src/index.ts';

import type { Narrative, UrgencyBucket, UrgencyGroup, UrgencyItem } from '../shared/insight/src/index.ts';

// ───────────────────────────── scalars ─────────────────────────────

/** `YYYY-MM-DD` in Asia/Jerusalem. Compared lexicographically. */
export type DayString = string;
/** `YYYY-MM`. */
export type MonthString = string;
/** ISO-8601 timestamp in UTC, e.g. `2026-09-06T03:12:00.000Z`. */
export type IsoTimestamp = string;
/** Integer agorot (₪ × 100). */
export type Agorot = number;
/** Integer minor units of a foreign currency (cents). */
export type Minor = number;
/** ISO-4217 code as delivered by Financy (`ILS`, `USD`, `EUR`, …). */
export type CurrencyCode = string;
/** Financy account id (opaque; may contain `=` — always `encodeURIComponent` in paths). */
export type AccountId = string;
/** Financy transaction id — 26-char base62, unique. */
export type TransactionId = string;
/** Financy connection id. */
export type ConnectionId = string;

// ───────────────────────────── enums (as const) ─────────────────────────────

export const PROVIDER_IDS = ['discount', 'max', 'cal'] as const;
/** Financy `providerId`. Unknown providers are passed through as plain strings. */
export type ProviderId = (typeof PROVIDER_IDS)[number] | (string & {});

export const CONNECTION_STATUSES = ['ACTIVE', 'COMPLETED', 'EXPIRED', 'REVOKED', 'FETCHING', 'REJECTED'] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const ACCOUNT_TYPES = ['CHECKING', 'SAVINGS', 'CARD', 'LOAN', 'SECURITY'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const CREDIT_STATUSES = ['enabled', 'blocked', 'deleted'] as const;
export type CreditStatus = (typeof CREDIT_STATUSES)[number];

export const LOAN_TYPES = ['RETAIL_LOAN', 'CREDIT_CARD', 'CREDIT_LIMIT'] as const;
export type LoanType = (typeof LOAN_TYPES)[number] | (string & {});

export type TransactionKind = 'CARD' | 'CHECKING';
export type TransactionStatus = 'BOOKED' | 'PENDING';

/** The 10 personal display groups (spec-personal §7.3 as superseded by ARCHITECTURE §4 mapping). */
export const GROUP_KEYS = ['HOUSING', 'GROCERIES', 'KIDS', 'TRANSPORT', 'DINING', 'SHOPPING', 'INSURANCE', 'HEALTH', 'TELECOM', 'OTHER'] as const;
export type GroupKey = (typeof GROUP_KEYS)[number];

/** How a row's `groupKey` was resolved: user override → merchant rule → static mapping. */
export type GroupSource = 'user' | 'merchant_rule' | 'mapped';

/** ARCHITECTURE §4 `spendingScope`. */
export const SCOPES = ['spend', 'income', 'transfer', 'card-charge', 'savings', 'tax', 'fee-return', 'ignore'] as const;
export type Scope = (typeof SCOPES)[number];

export type ReturnKind = 'principal' | 'fee';

export type ClassificationType = 'REGULAR_INCOME' | 'VARIABLE_INCOME' | 'REGULAR_EXPENSE' | 'VARIABLE_EXPENSE' | 'NEGATIVE_BEHAVIOR' | (string & {});

export type Role = 'owner' | 'member';

export type Severity = 'info' | 'warn' | 'critical';

export const ALERT_TYPES = [
  'card_due_soon',
  'category_over_baseline',
  'large_transaction',
  'connection_expired',
  'data_stale',
  'refresh_failed',
  'coverage_low',
  'returned_payment',
  'new_account',
] as const;
export type AlertType = (typeof ALERT_TYPES)[number];

export const ALERT_FILTERS = ['all', 'unread', 'cards', 'over', 'returns', 'conn'] as const;
export type AlertFilter = (typeof ALERT_FILTERS)[number];

/** Filter → alert types (spec-personal §4.7 + ARCHITECTURE §8). */
export const ALERT_FILTER_TYPES: Readonly<Record<Exclude<AlertFilter, 'all' | 'unread'>, readonly AlertType[]>> = {
  cards: ['card_due_soon', 'coverage_low'],
  over: ['category_over_baseline', 'large_transaction'],
  returns: ['returned_payment'],
  conn: ['connection_expired', 'data_stale', 'refresh_failed', 'new_account'],
};

export type AlertEntityType = 'card' | 'category' | 'transaction' | 'connection' | 'account' | 'sync' | 'coverage';

export type CycleStatus = 'open' | 'final' | 'charged';
export type CycleBy = 'tx' | 'booking';
export type DueDaySource = 'financy' | 'derived' | 'manual';

export type SyncKind = 'scheduled' | 'manual' | 'refresh' | 'noop';

export type FreshnessLevel = 'fresh' | 'stale_warn' | 'stale_critical' | 'none';

export type Theme = 'light' | 'dark' | 'auto';
export type TextSize = 'normal' | 'large';

// ───────────────────────────── errors ─────────────────────────────

export const ERROR_CODES = [
  'UNAUTHENTICATED', // 401 — no/expired session
  'INVALID_CREDENTIALS', // 401 — login or current password wrong
  'FORBIDDEN', // 403 — role (member on owner-only route)
  'CSRF', // 403 — missing `X-Requested-With: fetch` or bad Origin on a mutation
  'NOT_FOUND', // 404
  'METHOD_NOT_ALLOWED', // 405
  'VALIDATION', // 400 — body/query failed validation; `details.fields`
  'INVALID_PERIOD', // 400 — unparsable `period`, from > to, future month
  'PERIOD_TOO_LONG', // 400 — custom range > 366 days
  'INVALID_CURSOR', // 400
  'WEAK_PASSWORD', // 400 — < 10 chars
  'EMAIL_TAKEN', // 409
  'USER_LIMIT', // 409 — the personal system has at most 2 users
  'LAST_OWNER', // 409 — cannot delete/demote the only owner
  'CONFLICT', // 409 — generic optimistic-concurrency / state conflict
  'REFRESH_COOLDOWN', // 409 — provider refresh refused (ARCHITECTURE §7)
  'SYNC_IN_PROGRESS', // 409 — a sync run is already active
  'NO_ACTIVE_CONNECTIONS', // 409 — nothing to refresh (all EXPIRED/REVOKED/REJECTED)
  'PAYLOAD_TOO_LARGE', // 413 — JSON body > 1 MB
  'RATE_LIMITED', // 429 — login 5/min/IP, 20/h/email
  'FINANCY_UNAVAILABLE', // 502 — upstream error on a manual sync/refresh
  'INTERNAL', // 500
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiError {
  error: {
    code: ErrorCode;
    /** Hebrew, user-presentable. */
    message: string;
    /** Code-specific extras (see docs/api-personal.md §1.5). */
    details?: Record<string, unknown>;
  };
}

// ───────────────────────────── period ─────────────────────────────

/**
 * Query value of `period`. Semantics (docs/api-personal.md §2):
 *  - `month` (default) — the asOf month, 1st → asOf (MTD).
 *  - `YYYY-MM` — that month; if it is the asOf month → MTD, past → full month, future → 400.
 *  - `prev` — the full month before the asOf month.
 *  - `3m` / `12m` — the 3 / 12 FULL months before the asOf month.
 *  - `YYYY-MM-DD,YYYY-MM-DD` — custom, inclusive, ≤ 366 days, `to` clamped to asOf.
 */
export type PeriodParam = 'month' | 'prev' | '3m' | '12m' | MonthString | `${DayString},${DayString}`;

export type PeriodKey = 'month' | 'prev' | '3m' | '12m' | 'custom';

export interface PeriodRange {
  from: DayString;
  to: DayString;
}

/** The resolved period, echoed by every period-aware endpoint. @computed */
export interface Period extends PeriodRange {
  key: PeriodKey;
  /** Calendar months touched, ascending. */
  months: MonthString[];
  /** The month anomalies are judged on (last month in range). */
  lastMonth: MonthString;
  /** `lastMonth` bounds clamped to `to` (MTD when the range ends today). */
  lastFrom: DayString;
  lastTo: DayString;
  /** More than one calendar month. */
  multi: boolean;
  /** The range ends on asOf inside an unfinished month (MTD). */
  partial: boolean;
  /** Exactly one calendar month (full or MTD). */
  isMonth: boolean;
  /** Comparison range of the same length/shape, or null when it precedes available history. */
  prev: PeriodRange | null;
  /** First day for which transactions exist (sync window start). */
  historyFrom: DayString;
}

// ───────────────────────────── auth & users ─────────────────────────────

export interface User {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  createdAt: IsoTimestamp;
  lastLoginAt: IsoTimestamp | null;
}

export interface LoginRequest {
  email: string;
  password: string;
  /** 30-day sliding session instead of 12 h. Default false. */
  remember?: boolean;
}

export interface LoginResponse {
  user: User;
}

export interface MeResponse {
  user: User;
  /** Per-user appearance preferences (mirrored to localStorage by the client). @local */
  appearance: Appearance;
}

export interface ChangePasswordRequest {
  current: string;
  /** ≥ 10 characters. */
  next: string;
}

export interface OkResponse {
  ok: true;
}

export interface CreateUserRequest {
  email: string;
  displayName: string;
  /** ≥ 10 characters. */
  password: string;
  role: Role;
}

export interface UpdateUserRequest {
  displayName?: string;
  role?: Role;
  /**
   * Owner-only reset of **another** user's password (≥ 10 characters). A self-targeted reset is
   * refused with `400 VALIDATION` — changing your own password goes through
   * `POST /api/auth/password`, which verifies the current one.
   */
  password?: string;
  /**
   * The acting owner's **own** current password. Required whenever `password` is sent (step-up
   * re-authentication); wrong → `401 INVALID_CREDENTIALS`, throttled with `429 RATE_LIMITED`.
   */
  current?: string;
}

export interface UsersResponse {
  items: User[];
  /** Personal system: 2. */
  max: number;
}

// ───────────────────────────── connections & freshness ─────────────────────────────

export interface Connection {
  id: ConnectionId;
  providerId: ProviderId;
  status: ConnectionStatus;
  /** `PSD2` | `PLAID` | … */
  mode: string;
  /** History start of the consent. */
  startDate: DayString | null;
  expiryDate: DayString | null;
  /** Null on FETCHING/REJECTED. */
  lastFetchedAt: IsoTimestamp | null;
  lastFetchedDataDate: DayString | null;
  /** Financy `restrictedTo` (CACC, CARD, LOAN, SVGS, SCTS). */
  restrictedTo: string[];
  /** Financy counts as delivered. */
  counts: { accounts: number; cards: number; savings: number; loans: number; securities: number; transactions: number };
  /** Canonical accounts on this connection (after dedupe). @computed */
  accountIds: AccountId[];
  /** How many of them are assigned to this system. @computed */
  assignedAccountCount: number;
  /** Masked PANs from `access.balances[]` (cards), for the banner text. */
  maskedPans: string[];
  /** Expired/revoked/rejected → drives the global banner. @computed */
  needsRelink: boolean;
}

/** Compact connection row carried by GET /api/summary for the top bar and banner. */
export type ConnectionSummary = Pick<Connection, 'id' | 'providerId' | 'status' | 'lastFetchedAt' | 'expiryDate' | 'maskedPans' | 'needsRelink'>;

/** Freshness shown everywhere = MIN(lastFetchedAt) over assigned ACTIVE connections. @computed */
export interface Freshness {
  lastFetchedAt: IsoTimestamp | null;
  /** Hours between server now and `lastFetchedAt`; null when no ACTIVE connection. */
  ageHours: number | null;
  /** `fresh` (< staleHours) · `stale_warn` (≥ staleHours) · `stale_critical` (≥ 72 h) · `none`. */
  level: FreshnessLevel;
  /** From settings.alerts.staleHours (default 30). */
  staleHours: number;
  criticalHours: 72;
  /** Israel day of `lastFetchedAt` — the `asOf` every metric uses. */
  asOf: DayString;
}

export interface Credits {
  /** Σ sync_runs.credits_used this calendar month; null when unknown ("לא זמין"). */
  usedThisMonth: number | null;
  /** settings.plan or null ("לא זמין"). */
  plan: { tier: string; credits: number } | null;
  /** Cost of one provider refresh, shared by both systems. */
  perRefresh: 20;
}

// ───────────────────────────── accounts ─────────────────────────────

/** Disambiguated balance set (ARCHITECTURE §4). Signed as Financy delivers: CHECKING negative = overdraft; CARD/LOAN `booked` is a POSITIVE debt figure. */
export interface Balances {
  booked: Agorot | null;
  /** closingBooked with creditLimitIncluded=true (CHECKING adds the overdraft facility). Secondary text only. */
  bookedInclLimit: Agorot | null;
  forwardAvailable: Agorot | null;
  expected: Agorot | null;
  interimBooked: Agorot | null;
  interimAvailable: Agorot | null;
  /** Latest `referenceDate` among the rows. */
  referenceDate: DayString | null;
}

export interface InterestSummary {
  /** `FIXD` | `INDE` … */
  type: string | null;
  ratePct: number | null;
  /** e.g. `prime`. */
  relatedIndex: string | null;
}

/** @local — spec-personal §2.1 AccountNote (values Financy does not provide). */
export interface AccountNote {
  maturityDate: DayString | null;
  remainingPayments: number | null;
  monthlyPaymentAgorot: Agorot | null;
  note: string | null;
  updatedBy: string | null;
  updatedAt: IsoTimestamp | null;
}

/** Minimal account identity embedded in rows. */
export interface AccountRef {
  id: AccountId;
  type: AccountType;
  providerId: ProviderId;
  connectionId: ConnectionId;
  /** Financy accountName (e.g. `עו"ש`, `Max`). */
  name: string;
  /** Last 4 digits of the account number / masked PAN, or null. */
  numberLast4: string | null;
  /** Cards only. */
  creditStatus: CreditStatus | null;
  /** Status of the owning connection. @computed */
  connectionStatus: ConnectionStatus;
  /** `connection.lastFetchedAt` when the connection is not ACTIVE (→ "נתונים מ-dd.mm.yyyy" tag), else null. @computed */
  dataAsOf: IsoTimestamp | null;
}

export interface Account extends AccountRef {
  currency: CurrencyCode;
  /** Financy `product` (e.g. "דיסקונט בינלאומי"). */
  product: string | null;
  balances: Balances;
  /** Cards: card limit. */
  creditLimitAgorot: Agorot | null;
  /** CHECKING: overdraft facility (מסגרת). */
  overdraftLimitAgorot: Agorot | null;
  /** Financy `cardDueDate` as day of month, or null (Max/Discount). */
  cardDueDay: number | null;
  loanType: LoanType | null;
  contractStart: DayString | null;
  contractEnd: DayString | null;
  interest: InterestSummary | null;
  /** Canonical after dedupe (duplicates are never returned). */
  canonical: true;
  /** Assigned to this system (account_assignments). @local */
  assigned: boolean;
  note: AccountNote | null;
  /** Number of stored transactions. @computed */
  txCount: number;
  firstSeen: IsoTimestamp;
  updatedAt: IsoTimestamp;
  /** Display balance per spec §2.1: CHECKING → forwardAvailable ?? booked ("זמין"); others → booked. Liabilities are positive debt here. @computed */
  displayBalanceAgorot: Agorot | null;
  displayBalanceKind: 'available' | 'booked' | 'none';
}

export interface AccountsResponse {
  items: Account[];
}

export interface UpdateAccountRequest {
  assigned?: boolean;
  note?: Partial<Pick<AccountNote, 'maturityDate' | 'remainingPayments' | 'monthlyPaymentAgorot' | 'note'>>;
}

// ───────────────────────────── transactions ─────────────────────────────

export interface Installment {
  /** 1-based index of this payment. */
  n: number;
  total: number;
}

export interface UserCategoryMark {
  /** Display name of the user who set it. */
  by: string;
  at: IsoTimestamp;
  /** Set through "החל על כל העסקאות של ‹בית העסק›" (merchant_rules) rather than on this row. */
  viaMerchantRule: boolean;
}

export interface TransactionRow {
  id: TransactionId;
  account: AccountRef;
  kind: TransactionKind;
  /** Financy status — NOT "bookingDate is null". */
  status: TransactionStatus;
  /** The single bucketing date (ARCHITECTURE §4). */
  effDate: DayString;
  txDate: DayString | null;
  bookingDate: DayString | null;
  valueDate: DayString | null;
  /** Signed charged amount in agorot; null when charged currency ≠ ILS (excluded from every sum, "מטבע זר" chip). */
  amountAgorot: Agorot | null;
  /** Charged amount in its own currency, for the non-ILS case. */
  amountMinor: Minor;
  currency: CurrencyCode;
  /** Original amount when it differs from the charged currency (secondary line `$42.00`). */
  origAmountMinor: Minor | null;
  origCurrency: CurrencyCode | null;
  /** `merchantName ?? creditorName ?? debtorName ?? description`. */
  merchant: string;
  /** Normalized key used by merchant rules / recurrence detection. */
  merchantKey: string;
  /** Financy `description.initialClean`. */
  description: string;
  /** Financy category as delivered (null main on ~2% of rows). */
  category: { main: string | null; sub: string | null };
  /** Resolved display group. @computed */
  groupKey: GroupKey;
  groupSource: GroupSource;
  /** ARCHITECTURE §4 spendingScope. @computed */
  scope: Scope;
  installment: Installment | null;
  /** Card running balance / checking balance after the row, when Financy supplied it. */
  balanceEndDayAgorot: Agorot | null;
  classificationType: ClassificationType | null;
  /** `principal` / `fee` for HOK_RETURN / CHQ_RETURN / NEGATIVE_BEHAVIOR rows. @computed */
  returnKind: ReturnKind | null;
  /** For `scope = 'card-charge'` rows: the card whose cycle this charge paid, when matched. @computed */
  linkedCardAccountId: AccountId | null;
  /** For `scope = 'transfer'` rows: the counter-row on the other own account. @computed */
  transferPairId: TransactionId | null;
  /** @local user flags */
  note: string | null;
  /** User-marked "העברה פנימית" (forces scope transfer). */
  internal: boolean;
  userCategory: UserCategoryMark | null;
  /** PENDING row replaced by a BOOKED row (hidden unless `includeSuperseded=1`). */
  superseded: boolean;
  supersededBy: TransactionId | null;
  /** True for manual entries (POST /api/manual-entries) — never in Financy. */
  manual: boolean;
}

export interface TransactionsQuery {
  period?: PeriodParam;
  /** Override the period bounds (both or neither). */
  from?: DayString;
  to?: DayString;
  /** One canonical account id, or omitted for all assigned accounts. */
  account?: AccountId;
  /** Comma-separated GroupKeys. */
  cat?: string;
  /** Free text: merchant/description substring (case-insensitive) or an amount (`412.60`, `412`). */
  q?: string;
  status?: 'pending' | 'booked';
  dir?: 'credit' | 'debit';
  /** Extra scope filter. */
  kind?: 'returns' | 'internal' | 'income' | 'spend' | 'card-charge';
  /** Default `date`. */
  sort?: 'date' | 'amount';
  includeSuperseded?: '0' | '1';
  /** Default 50, max 200. */
  limit?: number;
  cursor?: string;
}

/** Filtered totals over the WHOLE result set (not just the page). @computed */
export interface TransactionsSummary {
  from: DayString;
  to: DayString;
  /** Σ |debits| over `spend` rows (flow rows only — internal, card-charge, returns excluded). */
  debitAgorot: Agorot;
  /** Σ credits over `income` rows (CHECKING). */
  creditAgorot: Agorot;
  /** Flow rows in the result. */
  count: number;
  pendingCount: number;
  /** All matched rows incl. internal/card-charge/returns (the list length). */
  totalRows: number;
}

/** Per-day totals for the days present on this page, computed over ALL rows of the day. @computed */
export interface DayTotal {
  day: DayString;
  /** Σ |debits| of flow rows that day (spec §4.2 day header). */
  debitAgorot: Agorot;
  rows: number;
}

export interface TransactionsResponse {
  period: Period;
  items: TransactionRow[];
  /** Opaque; pass back as `cursor`. Null on the last page. */
  nextCursor: string | null;
  summary: TransactionsSummary;
  dayTotals: DayTotal[];
}

export interface InstallmentPlanRef {
  merchant: string;
  merchantKey: string;
  totalCount: number;
  /** Payments already posted (incl. this one). */
  postedCount: number;
  remainingCount: number;
  /** Per-payment amount (magnitude). */
  amountAgorot: Agorot;
  /** Σ posted + projected, when all payments are equal; else null. */
  totalAgorot: Agorot | null;
  /** Projected next posting day / its card charge day. */
  nextDate: DayString | null;
  nextDueDate: DayString | null;
  /** Month of the final charge. */
  lastDueMonth: MonthString | null;
}

export interface TransactionDetail extends TransactionRow {
  installmentPlan: InstallmentPlanRef | null;
  /** Counter-row for transfers / matched card (for card-charge rows) / fee or principal sibling for returns. */
  related: TransactionRow[];
  /** Card cycle this purchase belongs to (CARD rows). @computed */
  cycle: { start: DayString; end: DayString; dueDate: DayString; status: CycleStatus } | null;
}

export interface UpdateTransactionRequest {
  /** New display group; `null` removes the user override (and the merchant rule when applyToMerchant). */
  groupKey?: GroupKey | null;
  /** Also create/replace the merchant rule for `merchantKey` (default false). */
  applyToMerchant?: boolean;
  note?: string | null;
  internal?: boolean;
}

export interface UpdateTransactionResponse {
  item: TransactionDetail;
  /** Rows whose resolved group changed (1 without applyToMerchant). */
  affected: number;
}

// ───────────────────────────── categories ─────────────────────────────

export interface Baseline {
  /** Mean of the previous 3 FULL months' spend, or the manual override. */
  agorot: Agorot;
  /** Months averaged (≤ 3), ascending; empty when manual. */
  months: MonthString[];
  n: number;
  /** Fewer than 3 months of history. */
  partial: boolean;
  manual: boolean;
}

/** One row of "הוצאות לפי קטגוריה מול הבסיס". @computed */
export interface CategoryStat {
  groupKey: GroupKey;
  /** Σ spend over the whole period (signed refunds reduce it). */
  amountAgorot: Agorot;
  /** Σ spend over `period.lastMonth` (== amountAgorot for single-month periods). */
  monthAmountAgorot: Agorot;
  baseline: Baseline;
  /** monthAmount / baseline; null when baseline is 0. */
  ratio: number | null;
  thresholdPct: number;
  /** baseline × (1 + thresholdPct/100). */
  thresholdAgorot: Agorot;
  over: boolean;
  /** null when not over. */
  severity: Severity | null;
  /** "בקצב הזה": monthAmount / monthProgress — only when day ≥ 10 of an MTD month, else null. */
  paceAgorot: Agorot | null;
  /** Transactions counted this month. */
  txCount: number;
  /** category_settings.hidden */
  hidden: boolean;
}

export interface CategoryMonth {
  month: MonthString;
  byGroup: Record<GroupKey, Agorot>;
  totalAgorot: Agorot;
  /** The asOf month (MTD). */
  partial: boolean;
}

export interface ReturnsMonth {
  month: MonthString;
  /** Number of bounced standing orders / cheques (principal rows). */
  count: number;
  feesAgorot: Agorot;
}

export interface ReturnsSummary {
  count: number;
  feesAgorot: Agorot;
}

export interface CategoriesResponse {
  period: Period;
  /** Sorted: over (by ratio desc) first, then by amount desc. */
  items: CategoryStat[];
  /** The 6 months ending in `period.lastMonth` (fewer when history is shorter) for the stacked chart. */
  monthly: CategoryMonth[];
  /** BRIEF §9.3 — same 6 months. */
  returns: { months: ReturnsMonth[]; total: ReturnsSummary };
}

export interface CategorySettings {
  groupKey: GroupKey;
  /** null → inherits settings.alerts.thresholdPct */
  thresholdPct: number | null;
  manualBaselineAgorot: Agorot | null;
  hidden: boolean;
  updatedBy: string | null;
  updatedAt: IsoTimestamp | null;
}

export interface SubcategoryStat {
  /** Financy `category.sub` (null → "ללא תת-קטגוריה"). Never rendered — it is the row key. */
  sub: string | null;
  /**
   * Hebrew label for the `main/sub` pair from `shared/financy/categories.ts` (the UI is Hebrew-only,
   * so a raw key like `BUSINESS_EXPENSES` must never reach the screen). `null` when `sub` is null or
   * the pair is not in the label map — the client then falls back to `he.cats.noSub` / the raw key.
   */
  label: string | null;
  amountAgorot: Agorot;
  /** 0..1 of the group's month total. */
  share: number;
  count: number;
}

export interface MerchantStat {
  merchant: string;
  merchantKey: string;
  amountAgorot: Agorot;
  count: number;
}

export interface TrendPoint {
  month: MonthString;
  amountAgorot: Agorot;
  /** Baseline as it was for that month (mean of the 3 months before it); null with no history. */
  baselineAgorot: Agorot | null;
  partial: boolean;
}

export interface CategoryDetailResponse {
  period: Period;
  stat: CategoryStat;
  settings: CategorySettings;
  /** 6 months ending in `period.lastMonth`. */
  trend: TrendPoint[];
  /** Over `period.lastMonth`, descending. */
  subcategories: SubcategoryStat[];
  /** Top 5 over `period.lastMonth`. */
  merchants: MerchantStat[];
}

export interface UpdateCategoryRequest {
  /** 0–100 in steps of 5; null → back to default. */
  thresholdPct?: number | null;
  /** > 0; null → automatic. */
  manualBaselineAgorot?: Agorot | null;
  hidden?: boolean;
}

// ───────────────────────────── cards ─────────────────────────────

/** @local + derivation state (ARCHITECTURE §8). */
export interface CardSettings {
  /** 1–28. Default per issuer: max 1 · cal 25 · isracard 1. */
  cutoffDay: number;
  /** 1–28. */
  dueDay: number;
  dueDaySource: DueDaySource;
  cycleBy: CycleBy;
  /** Checking account the charge is matched on (may be unassigned). */
  chargedFromAccountId: AccountId | null;
  /** Closed cycles matched to a bank charge (derivation confidence). */
  verifiedCycles: number;
  /** Optional user label. */
  label: string | null;
  updatedAt: IsoTimestamp | null;
}

export interface CardCycle {
  start: DayString;
  end: DayString;
  /** Due day of the following month, Fri/Sat → next Sunday. */
  dueDate: DayString;
  status: CycleStatus;
  /** Σ |card tx| in the cycle incl. PENDING. */
  amountAgorot: Agorot;
  /** Of which installment rows. */
  installmentAgorot: Agorot;
  /** Installment rows projected to post inside this cycle after asOf (open cycles). */
  futureInstallmentAgorot: Agorot;
  /** Open: max(accrued, variable forecast) + futureInstallment; closed: amount. */
  expectedAgorot: Agorot;
  txCount: number;
  /** Bank card-charge row that paid this cycle (status charged). */
  matchedChargeTxId: TransactionId | null;
  matchedChargeAgorot: Agorot | null;
  matchedChargeDate: DayString | null;
}

export interface InstallmentPlan extends InstallmentPlanRef {
  accountId: AccountId;
  firstDate: DayString;
  lastPostedDate: DayString | null;
  lastPostedN: number;
  groupKey: GroupKey;
}

export interface CardSummary {
  account: AccountRef;
  product: string | null;
  currency: CurrencyCode;
  creditStatus: CreditStatus;
  creditLimitAgorot: Agorot | null;
  /** |balances.booked| — spec: card liability = closingBooked. */
  usedAgorot: Agorot;
  /** used / limit, 0..1+; null without a limit. @computed */
  utilization: number | null;
  settings: CardSettings;
  /** Derivation could not confirm the due day ("יום חיוב לא אומת"). @computed */
  dueDayUnverified: boolean;
  /** Current (open) cycle, null for blocked/deleted cards. @computed */
  current: CardCycle | null;
  /** Closed and not yet charged ("סופי"), if any. @computed */
  final: CardCycle | null;
  /** Mean of last 3 closed cycles minus their installment rows. @computed */
  variableForecastAgorot: Agorot;
  /** Installments carried by this card (Cal only live; Max → `installmentsAvailable=false`). */
  installmentsAvailable: boolean;
  installments: {
    plans: number;
    monthlyAgorot: Agorot;
    lastDueMonth: MonthString | null;
  };
  /** Most recent charged cycle. @computed */
  lastCharged: CardCycle | null;
  txCount: number;
  /** Charged-from account is not assigned to this system → excluded from the overall coverage figure. */
  chargedFromAssigned: boolean | null;
}

export interface UpcomingCharge {
  accountId: AccountId;
  providerId: ProviderId;
  name: string;
  numberLast4: string | null;
  cycle: Pick<CardCycle, 'start' | 'end' | 'status'>;
  dueDate: DayString;
  /** Days from asOf; 0 = today. */
  daysUntil: number;
  knownAgorot: Agorot;
  expectedAgorot: Agorot;
  /** Inside the 30-day window. */
  inWindow: boolean;
  chargedFromAccountId: AccountId | null;
  chargedFromAssigned: boolean | null;
  connectionStatus: ConnectionStatus;
  dataAsOf: IsoTimestamp | null;
}

export interface CoverageAccount {
  accountId: AccountId;
  numberLast4: string | null;
  assigned: boolean;
  /** forwardAvailable ?? booked — never the creditLimitIncluded row. */
  availableAgorot: Agorot;
  availableSource: 'forwardAvailable' | 'booked';
  /** "כולל מסגרת ₪X" secondary text. */
  bookedInclLimitAgorot: Agorot | null;
  chargesKnownAgorot: Agorot;
  chargesExpectedAgorot: Agorot;
  coverageKnownAgorot: Agorot;
  coverageExpectedAgorot: Agorot;
}

/** "האם יש כיסוי?" @computed */
export interface Coverage {
  window: PeriodRange;
  /** Every not-yet-charged cycle of enabled assigned cards, sorted by dueDate (also those outside the window). */
  items: UpcomingCharge[];
  /** Per charged-from checking account (assigned and unassigned). */
  accounts: CoverageAccount[];
  /** Sums only charged-from accounts assigned to this system. */
  overall: {
    availableAgorot: Agorot;
    chargesKnownAgorot: Agorot;
    chargesExpectedAgorot: Agorot;
    coverageKnownAgorot: Agorot;
    coverageExpectedAgorot: Agorot;
    ok: boolean;
    okExpected: boolean;
  } | null;
  /** Cards whose charges land on an unassigned account ("יורד מהעו"ש ***6751 (לא משויך למערכת זו)"). */
  excluded: Array<{ accountId: AccountId; chargedFromAccountId: AccountId; chargedFromLast4: string | null }>;
  /** No enabled cards / all connections expired. */
  computable: boolean;
}

export interface ScheduleMonth {
  month: MonthString;
  knownAgorot: Agorot;
  forecastAgorot: Agorot;
  totalAgorot: Agorot;
  byCard: Record<AccountId, { knownAgorot: Agorot; forecastAgorot: Agorot }>;
}

/** 6-month installments schedule (spec §6.6). @computed */
export interface Schedule {
  months: ScheduleMonth[];
  totals: { knownAgorot: Agorot; forecastAgorot: Agorot; totalAgorot: Agorot };
  cards: Array<{ accountId: AccountId; variableForecastAgorot: Agorot }>;
}

export interface CardsResponse {
  asOf: DayString;
  /** Ordered enabled → blocked → deleted. */
  items: CardSummary[];
  coverage: Coverage;
  /** Enabled assigned cards only. */
  schedule: Schedule;
}

export interface CardDetailResponse {
  asOf: DayString;
  card: CardSummary;
  /** Last 6 closed + final + open, ascending by start. */
  cycles: CardCycle[];
  installmentPlans: InstallmentPlan[];
  /** This card only. */
  schedule: Schedule;
  /** 5 most recent rows. */
  recent: TransactionRow[];
  /** Issuer credit/installment loans shown under the card (BRIEF §9.4). */
  issuerLoans: Account[];
}

export interface UpdateCardRequest {
  cutoffDay?: number;
  dueDay?: number;
  cycleBy?: CycleBy;
  chargedFromAccountId?: AccountId | null;
  label?: string | null;
}

// ───────────────────────────── net worth ─────────────────────────────

export interface NetWorthPoint {
  /** Month the point closes, or the asOf month for the live point. */
  month: MonthString;
  /** End-of-month day, or asOf for the live point. */
  day: DayString;
  netAgorot: Agorot;
  assetsAgorot: Agorot;
  liabilitiesAgorot: Agorot;
  /** Any component carried forward / reconstructed (→ "אומדן"). */
  estimated: boolean;
  isCurrent: boolean;
}

export type NetWorthGroup = 'checking' | 'savings' | 'securities' | 'manual_asset' | 'bank_loan' | 'issuer_credit' | 'card' | 'closed_loan' | 'manual_liability';

export interface NetWorthAccount {
  accountId: AccountId | null;
  /** `manual:<id>` for manual balances. */
  manualEntryId: string | null;
  group: NetWorthGroup;
  type: AccountType | 'MANUAL';
  providerId: ProviderId | null;
  name: string;
  numberLast4: string | null;
  /** Positive magnitude on both sides. */
  balanceAgorot: Agorot;
  /** 0..1 of its side. */
  share: number;
  /** Balance today − balance at end of previous month; null without history. Liabilities: + = more debt. */
  monthChangeAgorot: Agorot | null;
  interest: InterestSummary | null;
  note: AccountNote | null;
  connectionStatus: ConnectionStatus | null;
  dataAsOf: IsoTimestamp | null;
  creditStatus: CreditStatus | null;
  loanType: LoanType | null;
  contractEnd: DayString | null;
  /** Card: final + accrued breakdown for the sub-line. */
  card: { finalAgorot: Agorot | null; finalDueDate: DayString | null; accruedAgorot: Agorot; accruedFrom: DayString | null } | null;
  /** Card's issuer loans whose balance is included in liabilities (BRIEF §9.4). */
  issuerLoanAccountIds: AccountId[];
}

export interface NetWorthResponse {
  asOf: DayString;
  netAgorot: Agorot;
  assetsAgorot: Agorot;
  liabilitiesAgorot: Agorot;
  monthChangeAgorot: Agorot;
  /** Percent, e.g. 0.7 for +0.7%; null when previous net is 0. */
  monthChangePct: number | null;
  /** vs the first history point; null with < 2 points. */
  yearChangeAgorot: Agorot | null;
  yearChangePct: number | null;
  /** 13 points (12 month-ends + current) when history allows. */
  history: NetWorthPoint[];
  /** "היסטוריה מ-dd.mm.yyyy" — first day with a real (non-estimated) balance for every account. */
  historyFrom: DayString;
  assets: NetWorthAccount[];
  liabilities: NetWorthAccount[];
  securities: { status: 'not_connected' };
}

export interface NetWorthHistoryResponse {
  asOf: DayString;
  points: NetWorthPoint[];
  historyFrom: DayString;
}

// ───────────────────────────── loans (REDESIGN §6) ─────────────────────────────

/** `bank` — a real bank loan. `card-credit` — a credit/installment facility at a card issuer ("קרדיט בכרטיס"). */
export const LOAN_KINDS = ['bank', 'card-credit'] as const;
export type LoanKind = (typeof LOAN_KINDS)[number];

/** `open` while the contract runs; `closed` once its end date has passed (19 live Cal facilities). */
export type LoanStatus = 'open' | 'closed';

/** Which system the account is assigned to (`account_assignments`) — drives `הכל · פרטי · עסקי`. */
export type LoanAssignment = 'personal' | 'business';

/** `fixed` — no index-linked component. `prime` — a rate linked to an index. `unknown` — the provider sent no `interest[]`. */
export type LoanRateStructure = 'fixed' | 'prime' | 'unknown';

/** Where `monthlyPaymentAgorot` came from: the user's note, the loan's own rows, amortization, or nowhere. */
export const LOAN_PAYMENT_SOURCES = ['note', 'history', 'amortized', 'none'] as const;
export type LoanPaymentSource = (typeof LOAN_PAYMENT_SOURCES)[number];

export const LOAN_SORTS = ['rate', 'balance', 'payment', 'end'] as const;
/** REDESIGN §6 sort switch. Default `rate` — "הכי יקר קודם". */
export type LoanSort = (typeof LOAN_SORTS)[number];

/** One `interest[]` row exactly as the provider delivers it, so the expanded row can show the structure. */
export interface LoanInterestTier {
  /** `FIXD` | `INDE` | whatever else arrives. */
  kind: string | null;
  /** `rate[].percentage` in percent units (live: 0 … 11.9). */
  ratePct: number | null;
  /** Amount tier bounds (`fromAmount`/`toAmount` × 100), null when the row is not tiered. */
  fromAgorot: Agorot | null;
  toAgorot: Agorot | null;
  /** `relatedIndices` present — the row follows an index (prime). */
  indexed: boolean;
  indexName: string | null;
  /** `toDateTime` as a day, when the provider dates the rate. */
  toDate: DayString | null;
  /** This tier covers the current balance (untiered rows always do). @computed */
  applicable: boolean;
}

/** The resolved rate and the structure behind it. @computed */
export interface LoanInterest {
  /** Effective annual rate: the FIXD row, plus the INDE row when that row is index-linked. */
  ratePct: number | null;
  structure: LoanRateStructure;
  fixedPct: number | null;
  indexPct: number | null;
  /** True when `indexPct` was ADDED into `ratePct` (Discount/Max "פריים + מרווח"); false when the index row is only a reference (Cal). */
  indexApplied: boolean;
  indexName: string | null;
  tiered: boolean;
  tiers: LoanInterestTier[];
}

/** The card a `card-credit` facility hangs off, matched by `(providerId, numberLast4)`. */
export interface LoanCardRef {
  accountId: AccountId;
  providerId: ProviderId;
  name: string;
  numberLast4: string | null;
  creditStatus: CreditStatus | null;
}

/** One row of "הכי יקר קודם". @computed */
export interface LoanRow {
  accountId: AccountId;
  providerId: ProviderId;
  connectionId: ConnectionId;
  /** Financy `accountName` — a human name for the loan. */
  name: string;
  product: string | null;
  numberLast4: string | null;
  kind: LoanKind;
  loanType: LoanType | null;
  status: LoanStatus;
  assignment: LoanAssignment;
  /** Remaining debt — always a POSITIVE magnitude (`|closingBooked|`). */
  balanceAgorot: Agorot;
  /** Card facilities: the facility's limit, when the provider sends one. */
  creditLimitAgorot: Agorot | null;
  /** Shorthand for `interest.ratePct` — the primary figure of the row. */
  ratePct: number | null;
  interest: LoanInterest;
  monthlyPaymentAgorot: Agorot | null;
  /** True whenever the payment was derived rather than given ("מוערך", REDESIGN §6). */
  monthlyPaymentEstimated: boolean;
  monthlyPaymentSource: LoanPaymentSource;
  startDate: DayString | null;
  /** `account_notes.maturityDate` when set, else the provider's `contractEndDate`. */
  endDate: DayString | null;
  /** Whole months from `asOf` to `endDate`; null when there is no end date. */
  endsInMonths: number | null;
  /** Interest over the next 12 months at the current rate and payment; null without a rate, null when closed. */
  estimatedAnnualInterestAgorot: Agorot | null;
  /** The next repayment day (the contract's day of month), for the home urgency feed. */
  nextPaymentDate: DayString | null;
  card: LoanCardRef | null;
  note: AccountNote | null;
  connectionStatus: ConnectionStatus;
  dataAsOf: IsoTimestamp | null;
  /** Stored rows on the loan account itself (0 for every live account — Financy sends none). */
  txCount: number;
}

/** Header figures over a set of loans. Debt, payment and interest count the OPEN ones only. @computed */
export interface LoansTotals {
  count: number;
  openCount: number;
  /** `סך החוב` */
  debtAgorot: Agorot;
  /** `תשלום חודשי` */
  monthlyPaymentAgorot: Agorot;
  /** How much of `monthlyPaymentAgorot` is derived rather than given. */
  monthlyPaymentEstimatedAgorot: Agorot;
  /** `ריבית משוקללת` — Σ(balance × rate) / Σ(balance); null when no loan has a rate. */
  weightedRatePct: number | null;
  /** `הריבית שתשלם השנה` */
  estimatedAnnualInterestAgorot: Agorot;
}

export interface LoanTrendPoint {
  month: MonthString;
  day: DayString;
  debtAgorot: Agorot;
  /** No snapshot for that month — the nearest known balance was carried ("אומדן"). */
  estimated: boolean;
  isCurrent: boolean;
}

/** Total debt over the last 12 months + the live point. @computed */
export interface LoanTrend {
  points: LoanTrendPoint[];
  /** Live debt − the previous month-end. */
  changeAgorot: Agorot;
  /** Mean change per month over the REAL points ("יורד ב-₪X בחודש בקצב הנוכחי"); null with < 2 real points. */
  avgMonthlyChangeAgorot: Agorot | null;
  historyFrom: DayString;
}

/** "בעוד {n} חודשים ייגמרו {k} הלוואות ויתפנו ₪{amount} בחודש." @computed */
export interface LoansEndingSoon {
  horizonMonths: number;
  count: number;
  monthlyFreedAgorot: Agorot;
  nextEndDate: DayString | null;
  lastEndDate: DayString | null;
  /** Whole months from `asOf` to `lastEndDate` — the `{n}` of the sentence. */
  withinMonths: number | null;
  accountIds: AccountId[];
}

/** Card facilities grouped under their card ("קרדיט בכרטיס"). @computed */
export interface LoanCardGroup {
  /** null when no CARD account matches the facility's `(providerId, numberLast4)`. */
  accountId: AccountId | null;
  providerId: ProviderId;
  name: string;
  numberLast4: string | null;
  creditStatus: CreditStatus | null;
  count: number;
  balanceAgorot: Agorot;
  monthlyPaymentAgorot: Agorot;
  accountIds: AccountId[];
}

export interface LoansQuery {
  sort?: LoanSort;
}

export interface LoansResponse {
  asOf: DayString;
  /** The sort applied to `items` (echo of `?sort=`, default `rate`). */
  sort: LoanSort;
  /** Every loan in the response (both assignments, both kinds). */
  totals: LoansTotals;
  byAssignment: Record<LoanAssignment, LoansTotals>;
  byKind: Record<LoanKind, LoansTotals>;
  trend: LoanTrend;
  /** Open loans, sorted. `CREDIT_LIMIT` (the overdraft facility) is never a loan and never here. */
  items: LoanRow[];
  /** Past their end date with a balance still on the books — the collapsed "הלוואות שנסגרו" group. */
  closed: LoanRow[];
  endingSoon: LoansEndingSoon;
  cards: LoanCardGroup[];
}

// ───────────────────────────── alerts ─────────────────────────────

export interface Alert {
  id: string;
  type: AlertType;
  severity: Severity;
  /** Hebrew (spec §5 templates). */
  title: string;
  body: string;
  entityType: AlertEntityType;
  /** accountId / groupKey / transactionId / connectionId / `yyyy-mm`. */
  entityId: string | null;
  amountAgorot: Agorot | null;
  createdAt: IsoTimestamp;
  readAt: IsoTimestamp | null;
  dismissedAt: IsoTimestamp | null;
  /** Auto-resolved because the trigger stopped holding (body ends with " · טופל"). */
  resolved: boolean;
}

export interface AlertsQuery {
  filter?: AlertFilter;
  includeDismissed?: '0' | '1';
  limit?: number;
  cursor?: string;
}

export interface AlertsResponse {
  items: Alert[];
  nextCursor: string | null;
  unreadCount: number;
  openCount: number;
}

export interface UpdateAlertRequest {
  read?: boolean;
  dismissed?: boolean;
}

export interface ReadAllAlertsRequest {
  filter?: AlertFilter;
}

export interface ReadAllAlertsResponse {
  updated: number;
  unreadCount: number;
}

// ───────────────────────────── settings ─────────────────────────────

export interface AlertRules {
  /** Days before a card charge (default 2). */
  cardDueDays: number;
  /** Default over-baseline threshold % (default 20). */
  thresholdPct: number;
  /** Single transaction alert (default 150000 = ₪1,500). */
  largeTxAgorot: Agorot;
  /** data_stale after N hours (default 30). */
  staleHours: number;
  /** Alert types the user switched off (connection_expired cannot be). */
  disabled: AlertType[];
}

export interface Appearance {
  theme: Theme;
  textSize: TextSize;
  /** "הצג מספרים מדויקים (אגורות) בכרטיסים". */
  exactNumbers: boolean;
}

export interface SettingsResponse {
  alerts: AlertRules;
  /** Defaults, for "ברירת מחדל: 2" links. */
  alertDefaults: AlertRules;
  appearance: Appearance;
  plan: Credits['plan'];
  sync: {
    everyMinutes: number;
    lastRunAt: IsoTimestamp | null;
    nextRunAt: IsoTimestamp | null;
  };
  /** Financy credentials present in .env (never the values). */
  financyConfigured: boolean;
  /** Owner-editable user list lives at /api/users; this is the count. */
  users: number;
}

export interface UpdateSettingsRequest {
  /** Owner only. */
  alerts?: Partial<AlertRules>;
  /** Any user — own preferences. */
  appearance?: Partial<Appearance>;
}

// ───────────────────────────── sync & SSE ─────────────────────────────

export interface SyncRun {
  id: number;
  kind: SyncKind;
  startedAt: IsoTimestamp;
  finishedAt: IsoTimestamp | null;
  ok: boolean | null;
  requestedBy: string | null;
  inserted: number;
  updated: number;
  superseded: number;
  creditsUsed: number;
  /** Hebrew, user-presentable; never a Financy body. */
  error: string | null;
}

export interface SyncRequestResponse {
  runId: number;
  kind: SyncKind;
  /** 20 with providerRefresh=1, else 0. */
  creditsEstimate: 0 | 20;
  startedAt: IsoTimestamp;
}

export interface SyncStatusResponse {
  running: boolean;
  current: SyncRun | null;
  last: SyncRun | null;
  lastSuccessful: SyncRun | null;
  nextScheduledAt: IsoTimestamp | null;
  freshness: Freshness;
  providerRefresh: {
    allowed: boolean;
    /** When `allowed=false`: earliest time a refresh would be accepted. */
    retryAt: IsoTimestamp | null;
    /** Why not (i18n key): `recent_fetch` | `recent_refresh` | `in_progress` | `no_active_connections`. */
    reason: 'recent_fetch' | 'recent_refresh' | 'in_progress' | 'no_active_connections' | null;
    creditsEstimate: 20;
  };
}

/** Server-Sent Events on GET /api/events. `event:` name → `data:` JSON. */
export type SseEvent =
  | { event: 'hello'; data: { serverTime: IsoTimestamp; freshness: Freshness } }
  | { event: 'sync:start'; data: { runId: number; kind: SyncKind } }
  | {
      event: 'sync:progress';
      data: { runId: number; step: 'connections' | 'accounts' | 'transactions' | 'reconcile' | 'snapshots' | 'alerts'; connectionId: ConnectionId | null; done: number; total: number };
    }
  | { event: 'sync:done'; data: { run: SyncRun; freshness: Freshness } }
  | { event: 'sync:error'; data: { runId: number; code: ErrorCode; message: string } }
  | { event: 'alerts:changed'; data: { unreadCount: number; openCount: number } }
  | { event: 'data:changed'; data: { scopes: Array<'summary' | 'transactions' | 'categories' | 'cards' | 'networth' | 'accounts' | 'connections' | 'settings'> } };

export type SseEventName = SseEvent['event'];

// ───────────────────────────── manual entries ─────────────────────────────

/**
 * @local — owner-entered values Financy cannot deliver.
 *  - `transaction`: a cash/other row (appears in lists and category sums with `manual: true`).
 *  - `balance`: an asset/liability figure (e.g. the not-connected securities portfolio) shown in net worth.
 */
export type ManualEntry =
  | {
      id: string;
      kind: 'transaction';
      effDate: DayString;
      /** Signed; negative = expense. */
      amountAgorot: Agorot;
      merchant: string;
      groupKey: GroupKey;
      note: string | null;
      createdBy: string;
      createdAt: IsoTimestamp;
    }
  | {
      id: string;
      kind: 'balance';
      side: 'asset' | 'liability';
      label: string;
      /** Positive magnitude. */
      balanceAgorot: Agorot;
      asOf: DayString;
      note: string | null;
      createdBy: string;
      createdAt: IsoTimestamp;
    };

export type CreateManualEntryRequest =
  | { kind: 'transaction'; effDate: DayString; amountAgorot: Agorot; merchant: string; groupKey: GroupKey; note?: string | null }
  | { kind: 'balance'; side: 'asset' | 'liability'; label: string; balanceAgorot: Agorot; asOf: DayString; note?: string | null };

export interface ManualEntriesResponse {
  items: ManualEntry[];
}

// ───────────────────────────── summary (home) ─────────────────────────────

export interface FlowFigures {
  inAgorot: Agorot;
  outAgorot: Agorot;
  /** in − out */
  netAgorot: Agorot;
  txCount: number;
}

/** Bank view: every CHECKING row on assigned accounts (what actually moved in the account). @computed */
export interface BankFlow extends FlowFigures {
  outBreakdown: {
    spendAgorot: Agorot;
    cardChargeAgorot: Agorot;
    taxAgorot: Agorot;
    savingsAgorot: Agorot;
    feeReturnAgorot: Agorot;
  };
}

/** Spending view: income vs `spend` rows on CHECKING + CARD by category (card purchases at purchase date). @computed */
export type SpendingFlow = FlowFigures;

export interface RecurringIncome {
  merchant: string;
  merchantKey: string;
  /** Day of month it usually lands. */
  day: number;
  amountAgorot: Agorot;
  /** Occurrences in the last 3 months. */
  occurrences: number;
  /** Next expected day (inside the current month, after asOf). */
  expectedDate: DayString;
}

export interface MonthProgress {
  day: number;
  daysInMonth: number;
  remaining: number;
  /** 0..1 */
  ratio: number;
  /** Next card charge inside the month for the progress-bar marker. */
  nextCharge: { dueDate: DayString; accountId: AccountId; knownAgorot: Agorot } | null;
}

export interface CashFlowHero {
  bank: BankFlow;
  bankPrev: BankFlow | null;
  spending: SpendingFlow;
  spendingPrev: SpendingFlow | null;
  /** Only for single-month MTD periods. */
  progress: MonthProgress | null;
  /** Income rows recurring on the same day (±3) in each of the last 3 months, still ahead this month. */
  expectedIncomes: RecurringIncome[];
  /** BRIEF §9.3 — the asOf month, independent of `period`. */
  returnsThisMonth: ReturnsSummary;
}

export interface NetWorthSummary {
  netAgorot: Agorot;
  assetsAgorot: Agorot;
  liabilitiesAgorot: Agorot;
  monthChangeAgorot: Agorot;
  monthChangePct: number | null;
  /** 13 points for the sparkline. */
  sparkline: NetWorthPoint[];
  securities: { status: 'not_connected' };
}

export interface AccountBalanceRow {
  account: AccountRef;
  product: string | null;
  displayBalanceAgorot: Agorot | null;
  displayBalanceKind: 'available' | 'booked' | 'none';
  /** CHECKING "כולל מסגרת" secondary text. */
  bookedInclLimitAgorot: Agorot | null;
}

export type SummaryState = 'ok' | 'no_data' | 'no_active_connections';

/** REDESIGN §2 — the home screen's time buckets, built by `shared/insight`. @computed */
export interface UrgencySummary {
  /** Every item, already in §2 order (bucket → late first → date asc → amount desc → id). */
  items: UrgencyItem[];
  /** The same items as sections. Buckets with no rows are ABSENT — never an empty heading. */
  groups: UrgencyGroup[];
  /** `{ now, week, month, later }` — for the `{n} דברים שאפשר לדחות` counter. */
  counts: Record<UrgencyBucket, number>;
}

/** The 4-line loans block of the home detail strip (REDESIGN §6). @computed */
export interface LoansSummary {
  totals: LoansTotals;
  endingSoon: LoansEndingSoon;
  /** Live debt − the previous month-end, so the strip can say which way it is going. */
  changeAgorot: Agorot;
}

export interface SummaryResponse {
  /** Israel day of `freshness.lastFetchedAt`; all metrics are relative to it. */
  asOf: DayString;
  serverTime: IsoTimestamp;
  period: Period;
  state: SummaryState;
  freshness: Freshness;
  connections: ConnectionSummary[];
  credits: Credits;
  cashflow: CashFlowHero;
  /** Sorted: over first (ratio desc), then amount desc; hidden groups excluded. */
  categories: CategoryStat[];
  /** Upcoming card charges + coverage ("חיובי אשראי קרובים"). */
  coverage: Coverage;
  netWorth: NetWorthSummary;
  alerts: {
    unreadCount: number;
    /** Unread open alerts, severity desc then createdAt desc, max 6 — the client hides `connection_expired` while the banner shows and takes 3. */
    unread: Alert[];
  };
  /** 5 most recent flow rows in the period (not internal/card-charge/returns). */
  recent: TransactionRow[];
  /** Assigned, non-deleted accounts for "החשבונות" (CHECKING, SAVINGS, CARD, LOAN order). */
  accounts: AccountBalanceRow[];
  user: Pick<User, 'id' | 'displayName' | 'role'>;

  /** REDESIGN §3 — "מה קורה", server-computed, deterministic, no model call. Always present. @computed */
  narrative: Narrative;
  /** REDESIGN §2. Always present; `items` may be empty. @computed */
  urgency: UrgencySummary;
  /** REDESIGN §6 — the loans line of the detail strip; the full screen is `GET /api/loans`. @computed */
  loans: LoansSummary;
}

// ───────────────────────────── export ─────────────────────────────

/** Columns of GET /api/export/transactions.csv, in order. */
export const CSV_COLUMNS = [
  'תאריך',
  'תאריך רישום',
  'חשבון',
  'ספק',
  'בית עסק',
  'תיאור',
  'קטגוריה (Financy)',
  'תת-קטגוריה (Financy)',
  'קבוצה',
  'סכום',
  'מטבע',
  'סכום מקורי',
  'מטבע מקורי',
  'תשלום',
  'סטטוס',
  'סיווג',
  'פנימי',
  'הערה',
  'מזהה',
] as const;

// ───────────────────────────── health ─────────────────────────────

/**
 * `GET /api/health` — unauthenticated liveness probe (ARCHITECTURE §13.1). It is the container
 * `HEALTHCHECK`, so it must answer before the first sync and while one is running, and it never
 * touches the Financy API. Served with `Cache-Control: no-store`.
 */
export interface HealthResponse {
  ok: boolean;
  system: 'personal' | 'business';
  version: string;
  db: 'ok' | 'error';
  /** ISO timestamp of the last successful sync run, or null when none has finished. */
  lastSyncAt: string | null;
}

// ───────────────────────────── route map (for api.ts) ─────────────────────────────

/** Method + path → request/response types. Path params are `encodeURIComponent`-ed by the client. */
export interface ApiRoutes {
  'GET /api/health': { res: HealthResponse };

  'POST /api/auth/login': { body: LoginRequest; res: LoginResponse };
  'POST /api/auth/logout': { body?: undefined; res: OkResponse };
  'GET /api/auth/me': { res: MeResponse };
  'POST /api/auth/password': { body: ChangePasswordRequest; res: OkResponse };

  'GET /api/summary': { query: { period?: PeriodParam }; res: SummaryResponse };

  'GET /api/transactions': { query: TransactionsQuery; res: TransactionsResponse };
  'GET /api/transactions/:id': { res: TransactionDetail };
  'PATCH /api/transactions/:id': { body: UpdateTransactionRequest; res: UpdateTransactionResponse };

  'GET /api/categories': { query: { period?: PeriodParam }; res: CategoriesResponse };
  'GET /api/categories/:key': { query: { period?: PeriodParam }; res: CategoryDetailResponse };
  'PATCH /api/categories/:key': { body: UpdateCategoryRequest; res: CategorySettings };

  'GET /api/cards': { res: CardsResponse };
  'GET /api/cards/:id': { res: CardDetailResponse };
  'PATCH /api/cards/:id': { body: UpdateCardRequest; res: CardDetailResponse };

  'GET /api/networth': { res: NetWorthResponse };
  'GET /api/networth/history': { query: { months?: number }; res: NetWorthHistoryResponse };

  'GET /api/loans': { query: LoansQuery; res: LoansResponse };

  'GET /api/accounts': { res: AccountsResponse };
  'PATCH /api/accounts/:id': { body: UpdateAccountRequest; res: Account };

  'GET /api/connections': { res: { items: Connection[] } };

  'GET /api/settings': { res: SettingsResponse };
  'PATCH /api/settings': { body: UpdateSettingsRequest; res: SettingsResponse };

  'GET /api/users': { res: UsersResponse };
  'POST /api/users': { body: CreateUserRequest; res: User };
  'PATCH /api/users/:id': { body: UpdateUserRequest; res: User };
  'DELETE /api/users/:id': { res: OkResponse };

  'POST /api/sync': { query: { providerRefresh?: '1' }; body?: undefined; res: SyncRequestResponse };
  'GET /api/sync/status': { res: SyncStatusResponse };

  'GET /api/alerts': { query: AlertsQuery; res: AlertsResponse };
  'PATCH /api/alerts/:id': { body: UpdateAlertRequest; res: Alert };
  'POST /api/alerts/read-all': { body: ReadAllAlertsRequest; res: ReadAllAlertsResponse };

  'GET /api/manual-entries': { res: ManualEntriesResponse };
  'POST /api/manual-entries': { body: CreateManualEntryRequest; res: ManualEntry };
  'DELETE /api/manual-entries/:id': { res: OkResponse };
}

export type ApiRouteKey = keyof ApiRoutes;
